/**
 * Animals: the wandering, the fleeing, and the sprites.
 *
 * Runs on the client for the same reason movement does — a few hundred chickens
 * pacing about is a rendering concern, not a simulation the server needs to
 * arbitrate. The server owns the only thing worth owning: what a kill drops
 * (backend/src/game/fauna/fauna.service.ts). The client names a species; it never
 * says what it got.
 *
 * The AI is deliberately small. Four states — idle, walking somewhere, fleeing,
 * dying — and one rule: a threat overrides whatever you were doing. Anything more
 * elaborate would be invisible at this zoom.
 *
 * Sprite convention (learned the hard way, see scripts/build-animal-sprites.py):
 * birds are SIDE-ONLY and get mirrored; quadrupeds have real side/down/up art —
 * which is NOT the player's down/side/up order.
 */
import { Container, Sprite, Texture, Rectangle } from 'pixi.js';
import { TILE, type Animal } from './world';

export type SpeciesId = Animal['species'];
type Facing = 'side' | 'down' | 'up';
type State = 'idle' | 'walk' | 'flee' | 'dead';

const BIRDS = new Set<SpeciesId>(['chicken', 'duck', 'swan']);
// Water birds live ON the water: they swim (not walk), treat land as impassable,
// and use the dedicated swim / swim_idle art.
const WATER_BIRDS = new Set<SpeciesId>(['duck', 'swan']);

/** Mirrors backend fauna.config.ts FAUNA_AI. Fetched at runtime; these are fallbacks. */
export interface FaunaTuning {
  spookRadius: number;
  skittishRadius: number;
  fleeTiles: number;
  calmMs: number;
  wander: { pauseMsMin: number; pauseMsMax: number; stepTiles: number };
  graze: { pauseMsMin: number; pauseMsMax: number; stepTiles: number };
  respawnMinDistance: number;
}

export const DEFAULT_TUNING: FaunaTuning = {
  spookRadius: 3,
  skittishRadius: 5,
  fleeTiles: 8,
  calmMs: 10_000,
  wander: { pauseMsMin: 1_500, pauseMsMax: 5_000, stepTiles: 3 },
  graze: { pauseMsMin: 4_000, pauseMsMax: 12_000, stepTiles: 1 },
  respawnMinDistance: 20,
};

export type Behaviour = 'wander' | 'graze' | 'flee' | 'skittish' | 'retaliate';

export interface SpeciesStat {
  id: SpeciesId;
  name: string;
  hp: number;
  behaviour: Behaviour;
  killable: boolean;
  retaliateDamage?: number;
}

/**
 * Fallback stats, mirroring backend fauna.config.ts SPECIES.
 *
 * These exist so a failed `/fauna` request degrades to "animals behave off stale
 * defaults" rather than "the world has no animals". The herd is generated from the
 * map seed and has nothing to do with an HTTP call; gating it on one meant a single
 * transient failure silently deleted ~1000 animals with no log and no error — the
 * map just looked empty.
 *
 * The server's copy still wins when it arrives. Drift here only affects a session
 * whose fetch failed, and only until the next load.
 */
export const DEFAULT_STATS: Record<string, SpeciesStat> = {
  chicken: { id: 'chicken', name: 'Chicken', hp: 8, behaviour: 'wander', killable: true },
  duck: { id: 'duck', name: 'Duck', hp: 10, behaviour: 'flee', killable: true },
  swan: { id: 'swan', name: 'Swan', hp: 14, behaviour: 'retaliate', killable: true, retaliateDamage: 3 },
  sheep: { id: 'sheep', name: 'Sheep', hp: 12, behaviour: 'graze', killable: true },
  cow: { id: 'cow', name: 'Cow', hp: 20, behaviour: 'graze', killable: true },
  horse: { id: 'horse', name: 'Horse', hp: 30, behaviour: 'skittish', killable: false },
};

/** Tiles/sec. Fleeing is faster than strolling — but well under the player's walk
 *  (4) so a walking player reliably runs one down to kill it. */
const SPEED = { walk: 1.1, flee: 2.6 } as const;
// §2.3: per-species render scale relative to the base PX, so animals read at
// sensible sizes next to the hero (chicken small, horse big) instead of uniform
// tiny. Multiplies the base PX (art is 32px on 48px tiles).
// Per-species render scale. Reduced after the horizontal-squish fix (which restored
// their full width) — at the old values a cow read "enormous". A cow is now ~1.4
// tiles, a bit smaller than the hero; chickens stay small.
const SPECIES_SCALE: Record<string, number> = {
  // Birds bumped up: the chicken/duck art fills little of its 32px frame, so 0.7 read
  // tinier than a mushroom. ~1.0 makes a chicken clearly readable while still the
  // smallest animal — the bigger mammals (approved sizes) stay as they were.
  chicken: 1.0, duck: 0.95, swan: 1.05, sheep: 1.15, cow: 1.4, horse: 1.8,
};
const ANIM_FPS = { idle: 4, walk: 8, flee: 12 } as const;
const HIT_FLASH_MS = 260;
const CORPSE_MS = 700;

export interface FaunaEntity {
  a: Animal;
  sprite: Sprite | null;   // null when outside the active window (frozen, no GPU cost)
  hp: number;
  state: State;
  /** World px. */
  x: number;
  y: number;
  homeX: number;
  homeY: number;
  facing: Facing;
  flipped: boolean;
  target: { x: number; y: number } | null;
  waitMs: number;
  calmMs: number;      // §2.2: no-re-flee cooldown after a flee (stays calm even if the player is near)
  fleeMs: number;      // §2.2: hard cap on how long a single flee runs
  animT: number;
  frame: number;
  hitMs: number;
  deadMs: number;
  diedOnDay: number;   // world day it died — revives on a later day, if the player is far
  stuckMs: number;     // time spent not making progress toward a target (unstuck)
  onWater?: boolean;   // water bird currently over a water tile? (drives swim vs. land anim)
}

export interface FaunaTextures {
  /** species_variant_anim[_dir] → frames */
  get(species: SpeciesId, variant: string, anim: string, dir: Facing | null): Texture[] | null;
}

/** Loads only what the world actually spawned, rather than all 331 frames. */
export async function loadFaunaTextures(animals: Animal[]): Promise<FaunaTextures> {
  const { Assets } = await import('pixi.js');
  const urls: Record<string, string> = {};
  const want = new Set<string>();
  for (const a of animals) want.add(`${a.species}:${a.variant}`);

  const ANIMS: Record<string, { anim: string; frames: number }[]> = {
    chicken: [{ anim: 'idle', frames: 2 }, { anim: 'walk', frames: 6 }, { anim: 'run', frames: 8 }, { anim: 'hit', frames: 4 }],
    duck: [{ anim: 'idle', frames: 2 }, { anim: 'walk', frames: 6 }, { anim: 'swim', frames: 4 }, { anim: 'swim_idle', frames: 2 }, { anim: 'hit', frames: 4 }],
    swan: [{ anim: 'idle', frames: 2 }, { anim: 'walk', frames: 6 }, { anim: 'swim', frames: 3 }, { anim: 'swim_idle', frames: 2 }, { anim: 'hit', frames: 4 }],
    sheep: [{ anim: 'idle', frames: 2 }, { anim: 'walk', frames: 8 }, { anim: 'hit', frames: 4 }],
    cow: [{ anim: 'idle', frames: 2 }, { anim: 'walk', frames: 8 }, { anim: 'hit', frames: 4 }],
    horse: [{ anim: 'idle', frames: 2 }, { anim: 'walk', frames: 6 }, { anim: 'hit', frames: 4 }],
  };
  const DIRS: Facing[] = ['side', 'down', 'up'];

  for (const key of want) {
    const [species, variant] = key.split(':');
    const bird = BIRDS.has(species as SpeciesId);
    for (const { anim, frames } of ANIMS[species] ?? []) {
      for (const d of bird ? [null] : DIRS) {
        for (let f = 0; f < frames; f++) {
          const name = d
            ? `${species}_${variant}_${anim}_${d}_${f}`
            : `${species}_${variant}_${anim}_${f}`;
          urls[name] = `/sprites/animals/${name}.png`;
        }
      }
    }
  }
  const loaded = (await Assets.load(Object.values(urls))) as Record<string, Texture>;
  const tex: Record<string, Texture> = {};
  for (const [k, u] of Object.entries(urls)) tex[k] = loaded[u] ?? Texture.from(u);

  return {
    get(species, variant, anim, dir) {
      const out: Texture[] = [];
      for (let f = 0; ; f++) {
        const name = dir ? `${species}_${variant}_${anim}_${dir}_${f}` : `${species}_${variant}_${anim}_${f}`;
        const t = tex[name];
        if (!t) break;
        out.push(t);
      }
      return out.length ? out : null;
    },
  };
}

const rand = (a: number, b: number) => a + Math.random() * (b - a);

/**
 * Open water for a swimming bird: this tile AND its four neighbours are water.
 *
 * OWNER (2026-08-17): "ducks and swans still swim over the ground". The old test
 * was `isWater(feet)` alone, which admits the WATERLINE cell — a water tile with
 * land beside it. That cell is not drawn as water: views/farm/scene.ts paints
 * shore-water tiles with whole 16px waterline pieces (lawn, stone lip, then the
 * foam — see the shoreKeys comment), so the cell is mostly LAND pixels. A bird
 * standing in it is rendered on grass and rock, which is exactly what the owner
 * is looking at. Measured on the real generator: 35 of 64 water birds spawn on a
 * waterline tile (fauna-coastline.test.ts).
 *
 * Requiring the 4-neighbourhood keeps the whole 32px sprite (which is ~1 tile wide
 * and half a tile tall at its render scale) over cells that are painted as open
 * water, with no waterline art under any part of it.
 */
export function openWater(isWater: (x: number, y: number) => boolean, x: number, y: number): boolean {
  return isWater(x, y)
    && isWater(x + TILE, y) && isWater(x - TILE, y)
    && isWater(x, y + TILE) && isWater(x, y - TILE);
}

export interface Fauna {
  entities: FaunaEntity[];
  /** Per frame. `blocked` is in world px and probes the animal's feet. */
  update(dt: number, playerX: number, playerY: number, dayNumber: number, blocked: (x: number, y: number) => boolean, isWater?: (x: number, y: number) => boolean): void;
  /** Hit whatever is under this world point. Returns the entity if it died. */
  hitAt(wx: number, wy: number, damage: number): { entity: FaunaEntity; died: boolean } | null;
  destroy(): void;
}

export function createFauna(
  animals: Animal[],
  tex: FaunaTextures,
  stats: Record<string, SpeciesStat>,
  tuning: FaunaTuning = DEFAULT_TUNING,
  host?: Container,
): Fauna {
  // Animals mount into the shared y-sorted entity layer (host) so they sort with
  // trees/rocks by feet-Y — a dedicated sub-container sat at a single zIndex and made
  // every animal draw BEHIND all decor (it looked like they were stuck in the trees).
  // Falls back to an own container only if no host is given (kept for safety/tests).
  const ownLayer = host ?? new Container();
  ownLayer.sortableChildren = true;
  const mount = ownLayer;
  const entities: FaunaEntity[] = [];
  let lastDay = 0;   // most recent world day seen by update(), read by hitAt()
  let lastBlocked: (x: number, y: number) => boolean = () => false;   // captured for hitAt's flee
  // Also captured for hitAt: a struck DUCK must flee by the swimming rules. It
  // used to pick its escape route with the land predicate, which reads every water
  // tile as blocked, so the nav-validated search always failed and fell back to the
  // raw away-vector — straight at the bank.
  let lastIsWater: ((x: number, y: number) => boolean) | undefined;

  const PX = TILE / 32; // animal frames are 32px; the world runs at 48px tiles
  // Streaming window tied to the fog's VISIBLE radius: an animal only gets a Sprite
  // (and is updated) while it's within ~the lit/clear area around the player. In the
  // remembered "grey" fog and the unexplored black you neither see nor track moving
  // creatures, so out here the entity keeps its cheap data, drops its sprite, and
  // freezes. Fog clear ≈ 7–9 tiles; 12 gives a small buffer so they fade in at the
  // edge of sight rather than popping. (Trees/terrain use a larger "remembered" cull.)
  const WINDOW_HALF = 12;

  // Build the cheap ENTITY for every animal; the Sprite is created lazily by the
  // window in update() (see spawnSprite), so nothing off-screen is resident.
  for (const a of animals) {
    const st = stats[a.species];
    if (!st) continue;
    if (!tex.get(a.species, a.variant, 'idle', BIRDS.has(a.species) ? null : 'side')) continue;
    const x = (a.tx + 0.5) * TILE;
    const y = (a.ty + 0.9) * TILE;
    entities.push({
      a, sprite: null, hp: st.hp, state: 'idle',
      x, y, homeX: x, homeY: y,
      facing: 'side', flipped: false,
      target: null,
      waitMs: rand(0, 3000),
      calmMs: 0, fleeMs: 0, animT: 0, frame: 0, hitMs: 0, deadMs: 0, diedOnDay: -1, stuckMs: 0,
    });
  }

  /** Create the Sprite for an entity entering the window (mirrors the old build). */
  const spawnSprite = (e: FaunaEntity) => {
    const frames = tex.get(e.a.species, e.a.variant, 'idle', BIRDS.has(e.a.species) ? null : 'side');
    if (!frames) return;
    const sprite = new Sprite(frames[e.frame % frames.length] ?? frames[0]);
    sprite.anchor.set(0.5, 0.9);
    sprite.scale.set(PX * (SPECIES_SCALE[e.a.species] ?? 1.4));
    sprite.x = e.x; sprite.y = e.y; sprite.zIndex = e.y;
    sprite.eventMode = 'static';
    sprite.cursor = 'pointer';
    sprite.hitArea = new Rectangle(-16, -29, 32, 32);
    mount.addChild(sprite);
    e.sprite = sprite;
  };
  const despawnSprite = (e: FaunaEntity) => { e.sprite?.destroy(); e.sprite = null; };

  const animOf = (e: FaunaEntity): string => {
    if (e.hitMs > 0) return 'hit';
    // A water bird only shows its swim frames (which carry a water ripple base)
    // when it is actually over water — on grass/earth it waddles with the land
    // walk/idle art, so no water shows under it. onWater undefined ⇒ assume water
    // (preserves behaviour when no isWater test is supplied).
    const water = WATER_BIRDS.has(e.a.species) && e.onWater !== false;
    if (e.state === 'flee') return water ? 'swim' : e.a.species === 'chicken' ? 'run' : 'walk';
    if (e.state === 'walk') return water ? 'swim' : 'walk';
    return water ? 'swim_idle' : 'idle';
  };

  const setFrames = (e: FaunaEntity, dt: number) => {
    if (!e.sprite) return;
    const bird = BIRDS.has(e.a.species);
    const anim = animOf(e);
    const frames = tex.get(e.a.species, e.a.variant, anim, bird ? null : e.facing)
      ?? tex.get(e.a.species, e.a.variant, 'idle', bird ? null : 'side');
    if (!frames) return;
    const fps = e.state === 'flee' ? ANIM_FPS.flee : e.state === 'walk' ? ANIM_FPS.walk : ANIM_FPS.idle;
    e.animT += dt;
    if (e.animT >= 1 / fps) { e.animT = 0; e.frame++; }
    e.sprite.texture = frames[e.frame % frames.length];
    // Birds have no up/down art, so a bird walking north is just a mirrored
    // side-view. Quadrupeds mirror only their side art. NOTE: keep the per-species
    // scale here — resetting scale.x to a bare ±PX (dropping SPECIES_SCALE) squished
    // every animal horizontally (a cow at 2.0 became half-width) each frame.
    const sc = PX * (SPECIES_SCALE[e.a.species] ?? 1.4);
    e.sprite.scale.x = (e.flipped ? -1 : 1) * sc;
  };

  // The animal side art faces LEFT by default (verified against the cow/chicken/sheep
  // sheets) — the OPPOSITE of the player, whose side art faces right. So the sprite is
  // flipped when moving RIGHT (dx > 0). Getting this backwards made every animal
  // moonwalk — sliding one way while facing the other.
  const faceTo = (e: FaunaEntity, dx: number, dy: number) => {
    if (BIRDS.has(e.a.species)) {
      if (Math.abs(dx) > 0.01) e.flipped = dx > 0;
      return;
    }
    if (Math.abs(dx) > Math.abs(dy)) { e.facing = 'side'; e.flipped = dx > 0; }
    else { e.facing = dy < 0 ? 'up' : 'down'; e.flipped = false; }
  };

  const pickWander = (e: FaunaEntity, st: SpeciesStat) => {
    const cfg = st.behaviour === 'graze' ? tuning.graze : tuning.wander;
    const ang = Math.random() * Math.PI * 2;
    const d = rand(1, cfg.stepTiles) * TILE;
    e.target = { x: e.homeX + Math.cos(ang) * d, y: e.homeY + Math.sin(ang) * d };
    e.state = 'walk';
  };

  // §2.2: pick a flee target that runs AWAY but is nav-validated — never straight
  // into a blocker or the map edge. Tries the away-vector first, then rotations
  // (steer along a wall) with a small random zig-zag so it isn't a dead-straight
  // sprint. Falls back to the raw away-vector only if nothing is clear.
  const validFleeTarget = (e: FaunaEntity, ax: number, ay: number, blocked: (x: number, y: number) => boolean) => {
    const base = Math.atan2(ay, ax);
    const jitter = (Math.random() - 0.5) * 0.5;           // ±0.25 rad zig-zag
    const dist = tuning.fleeTiles * TILE;
    const clearAlong = (a: number) => {
      for (let t = 1.5; t <= tuning.fleeTiles; t += 1.5)   // sample a few points along the ray
        if (blocked(e.x + Math.cos(a) * t * TILE, e.y + Math.sin(a) * t * TILE)) return false;
      return true;
    };
    for (const off of [0, 0.6, -0.6, 1.2, -1.2, 1.8, -1.8, 2.6, -2.6]) {
      const a = base + off + jitter;
      if (clearAlong(a)) return { x: e.x + Math.cos(a) * dist, y: e.y + Math.sin(a) * dist };
    }
    const m = Math.hypot(ax, ay) || 1;
    return { x: e.x + (ax / m) * dist, y: e.y + (ay / m) * dist };
  };
  /**
   * Move a water bird off the waterline and onto open water.
   *
   * Samples nearby points rather than scanning the map: fauna.ts is given only a
   * predicate, not the terrain. Home moves with it, otherwise pickWander would
   * keep aiming the bird back at the shore tile it was seeded on. If nothing open
   * is within range (a one-tile puddle) it simply keeps its old behaviour — there
   * is nowhere better to send it.
   */
  const seekOpenWater = (e: FaunaEntity, isWater: (x: number, y: number) => boolean): boolean => {
    for (let i = 0; i < 16; i++) {
      const a = Math.random() * Math.PI * 2;
      const d = rand(1.5, 5) * TILE;
      const x = e.x + Math.cos(a) * d, y = e.y + Math.sin(a) * d;
      if (openWater(isWater, x, y)) {
        e.target = { x, y };
        e.homeX = x; e.homeY = y;
        e.state = 'walk';
        return true;
      }
    }
    return false;
  };

  const startFlee = (e: FaunaEntity, fromX: number, fromY: number, blocked: (x: number, y: number) => boolean) => {
    if (e.calmMs > 0) return;                              // recently fled → stay calm (catchable)
    e.target = validFleeTarget(e, e.x - fromX, e.y - fromY, blocked);
    e.state = 'flee';
    e.calmMs = tuning.calmMs;                             // 10s: no re-flee during this window
    e.fleeMs = 3000;                                      // hard 3s cap on the run
  };

  return {
    entities,
    update(dt, px, py, dayNumber, blocked, isWater) {
      lastDay = dayNumber;
      lastBlocked = blocked;
      lastIsWater = isWater;
      const ptx = px / TILE, pty = py / TILE;
      for (const e of entities) {
        const st = stats[e.a.species];
        if (!st) continue;

        // Water birds swim: passability is inverted — OPEN water is passable,
        // land and the waterline cell block (see openWater: the waterline is
        // drawn as shore art, so a bird in it reads as standing on the ground).
        //
        // A bird that is not already in open water — world-gen seeds ducks in the
        // shallows, waterline cells included — must be allowed to cross the
        // waterline to get out, or it would be walled in on the very tile we are
        // trying to move it off. So while stranded the loose test applies and the
        // seek below aims it at open water.
        const swimming = WATER_BIRDS.has(e.a.species) && !!isWater;
        const inOpen = swimming && openWater(isWater!, e.x, e.y);
        const blk = !swimming ? blocked
          : inOpen
            ? (x: number, y: number) => !openWater(isWater!, x, y)
            : (x: number, y: number) => !isWater!(x, y);

        // ── Streaming window: freeze + drop the sprite for anything far from the
        // player. Its data stays; it simply stops existing on the GPU and stops
        // moving until the player comes back near.
        const inWindow = Math.abs(e.x / TILE - ptx) <= WINDOW_HALF && Math.abs(e.y / TILE - pty) <= WINDOW_HALF;
        if (!inWindow) {
          if (e.sprite) despawnSprite(e);
          // Dead animals still repopulate while you're away (so the world isn't empty
          // when you return): the corpse "fades" invisibly, then revives with NO
          // sprite; the window creates one when the player comes back near.
          if (e.state === 'dead') {
            if (e.deadMs > 0) e.deadMs -= dt * 1000;
            else if (dayNumber > e.diedOnDay
              && Math.hypot(px - e.homeX, py - e.homeY) / TILE >= tuning.respawnMinDistance) {
              e.hp = st.hp; e.state = 'idle'; e.x = e.homeX; e.y = e.homeY;
              e.waitMs = rand(500, 3000); e.diedOnDay = -1;
            }
          }
          continue;
        }

        if (e.hitMs > 0) e.hitMs -= dt * 1000;

        if (e.state === 'dead') {
          if (e.deadMs > 0) {
            if (!e.sprite) spawnSprite(e);                  // corpse still fading — needs its sprite
            e.deadMs -= dt * 1000;
            if (e.sprite) { e.sprite.alpha = Math.max(0, e.deadMs / CORPSE_MS); if (e.deadMs <= 0) e.sprite.visible = false; }
            continue;
          }
          // Faded away. Revive at home on a LATER day, but never in the player's
          // lap — the herd should reappear while you're elsewhere, not pop in
          // beside you (brief: once per day, ≥20 tiles from the player). No sprite
          // is needed until it actually revives.
          const far = Math.hypot(px - e.homeX, py - e.homeY) / TILE >= tuning.respawnMinDistance;
          if (dayNumber > e.diedOnDay && far) {
            e.hp = st.hp;
            e.state = 'idle';
            e.x = e.homeX; e.y = e.homeY;
            if (!e.sprite) spawnSprite(e);
            if (e.sprite) { e.sprite.x = e.x; e.sprite.y = e.y; e.sprite.visible = true; e.sprite.alpha = 1; e.sprite.eventMode = 'static'; }
            e.waitMs = rand(500, 3000);
            e.diedOnDay = -1;
          }
          continue;
        }

        // Alive: ensure a sprite exists (created when it enters the window).
        if (!e.sprite) spawnSprite(e);
        if (!e.sprite) continue;   // texture missing

        // A threat overrides whatever it was doing. Grazers and wanderers spook
        // at 3 tiles; the skittish horse keeps a wider berth.
        const dist = Math.hypot(px - e.x, py - e.y) / TILE;
        const spook = st.behaviour === 'skittish' ? tuning.skittishRadius : tuning.spookRadius;
        // A swan doesn't run from you — it runs AT you (handled on hit); it only
        // strolls otherwise.
        const runs = st.behaviour !== 'retaliate';
        // §2.2: only flee if not already fleeing AND not in the post-flee calm window.
        if (runs && dist < spook && e.state !== 'flee' && e.calmMs <= 0) startFlee(e, px, py, blk);

        if (e.calmMs > 0) e.calmMs -= dt * 1000;
        if (e.state === 'flee') {
          e.fleeMs -= dt * 1000;                          // §2.2: hard 3s cap on a flee
          if (e.fleeMs <= 0) { e.state = 'idle'; e.target = null; e.waitMs = rand(500, 2000); }
        }

        // Stranded on the coastline (seeded there, or pushed there before this
        // rule existed): head for open water instead of pottering about on the
        // shore art. Overrides the wander so it actually leaves.
        if (swimming && !inOpen && !e.target) seekOpenWater(e, isWater!);

        if (e.state === 'idle') {
          e.waitMs -= dt * 1000;
          if (e.waitMs <= 0) pickWander(e, st);
        }

        if (e.target) {
          const speed = (e.state === 'flee' ? SPEED.flee : SPEED.walk) * TILE;
          const dx = e.target.x - e.x, dy = e.target.y - e.y;
          const d = Math.hypot(dx, dy);
          if (d < 2) {
            const wasFlee = e.state === 'flee';           // §2.2: reaching the flee target ends the flee
            e.target = null;
            e.state = 'idle';
            const cfg = st.behaviour === 'graze' ? tuning.graze : tuning.wander;
            e.waitMs = wasFlee ? rand(500, 2000) : rand(cfg.pauseMsMin, cfg.pauseMsMax);
          } else {
            const step = Math.min(d, speed * dt);
            const nx = e.x + (dx / d) * step;
            const ny = e.y + (dy / d) * step;
            const ox = e.x, oy = e.y;
            // Slide along obstacles rather than shivering against them; if both
            // axes are blocked, give up on this target and pick another.
            if (!blk(nx, ny)) { e.x = nx; e.y = ny; }
            else if (!blk(nx, e.y)) e.x = nx;
            else if (!blk(e.x, ny)) e.y = ny;
            else e.target = null;
            faceTo(e, dx, dy);
            // Unstuck: if it's sliding against decor without real progress (a wall
            // between it and its target), drop the target after ~1.2s and re-idle so
            // it never freezes wedged inside a tree/rock.
            if (Math.hypot(e.x - ox, e.y - oy) < step * 0.35) {
              e.stuckMs += dt * 1000;
              if (e.stuckMs > 1200) { e.stuckMs = 0; e.target = null; e.state = 'idle'; e.waitMs = rand(300, 1200); }
            } else e.stuckMs = 0;
          }
        }

        e.sprite.x = e.x;
        e.sprite.y = e.y;
        e.sprite.zIndex = e.y;
        e.sprite.tint = e.hitMs > 0 ? 0xff9999 : 0xffffff;
        // Track whether the bird is over water this frame → swim vs. land anim.
        if (WATER_BIRDS.has(e.a.species)) e.onWater = isWater ? isWater(e.x, e.y) : true;
        setFrames(e, dt);
      }
    },
    hitAt(wx, wy, damage) {
      // Nearest living animal whose sprite covers the point.
      let best: FaunaEntity | null = null;
      let bestD = Infinity;
      for (const e of entities) {
        if (e.state === 'dead') continue;
        const dx = Math.abs(wx - e.x), dy = wy - e.y;
        if (dx > 18 || dy > 6 || dy < -32) continue;
        const d = dx + Math.abs(dy);
        if (d < bestD) { bestD = d; best = e; }
      }
      if (!best) return null;
      const st = stats[best.a.species];
      if (!st.killable) return { entity: best, died: false };   // horses are tamed, not felled
      best.hp -= damage;
      best.hitMs = HIT_FLASH_MS;
      if (best.state !== 'flee') {
        best.calmMs = 0;
        const fleeBlk = WATER_BIRDS.has(best.a.species) && lastIsWater
          ? (x: number, y: number) => !openWater(lastIsWater!, x, y)
          : lastBlocked;
        startFlee(best, wx, wy, fleeBlk);                 // being hit always spooks
      }
      if (best.hp <= 0) {
        best.state = 'dead';
        best.deadMs = CORPSE_MS;
        best.diedOnDay = lastDay;
        if (best.sprite) best.sprite.eventMode = 'none';
        return { entity: best, died: true };
      }
      return { entity: best, died: false };
    },
    destroy() {
      // Sprites live in the shared host layer now — destroy them individually so we
      // don't tear down the whole entity layer.
      for (const e of entities) e.sprite?.destroy();
      if (!host) ownLayer.destroy({ children: true });
      entities.length = 0;
    },
  };
}
